import { getSession } from '../auth';
import { toast } from '../components/toast';
import { assignLead, isProductionData, listLeadAssignableMembers, listLeads, updateLeadStatus, type LeadAssignee } from '../lib/repository';
import type { LeadRecord } from '../domain';

let productionLeads: LeadRecord[] | null = null;
let leadsLoading = false;
let productionMembers: LeadAssignee[] = [];
let membersLoaded = false;
let membersLoading = false;

const STAGES = ['new', 'contacted', 'qualified', 'won', 'lost'] as const;

function loadLeads(): void {
  if (!isProductionData() || productionLeads || leadsLoading) return;
  leadsLoading = true;
  listLeads()
    .then((rows) => { productionLeads = rows; leadsLoading = false; (window as any).__rerender?.(); })
    .catch(() => { productionLeads = []; leadsLoading = false; });
}
function loadMembers(): void {
  if (!isProductionData() || membersLoaded || membersLoading) return;
  membersLoading = true;
  listLeadAssignableMembers()
    .then((rows) => { productionMembers = rows; membersLoaded = true; membersLoading = false; (window as any).__rerender?.(); })
    .catch(() => { productionMembers = []; membersLoaded = true; membersLoading = false; });
}

export function renderLeadsInbox(): string {
  void loadLeads(); void loadMembers();
  const leads = productionLeads || [];
  const active = ((location.hash.match(/stage=([a-z]+)/) || [])[1] || 'new') as typeof STAGES[number];
  const stage = (STAGES as readonly string[]).includes(active) ? active : 'new';
  const filtered = leads.filter((l) => l.status === stage);
  const cards = filtered.map((l) => {
    const assigned = productionMembers.find((m) => m.uid === l.ownerUid);
    const assignee = l.ownerUid
      ? (assigned ? assigned.name + ' (' + assigned.role + ')' : 'UID ' + l.ownerUid.slice(0, 6))
      : 'Unassigned';
    const opts = productionMembers.map((m) => '<option value="' + m.uid + '"' + (m.uid === l.ownerUid ? ' selected' : '') + '>' + escapeHtml(m.name) + ' · ' + m.role + '</option>').join('');
    return '<article class="card lead-card" data-lead-id="' + l.id + '" style="padding:14px;margin-bottom:10px">' +
      '<div class="row" style="margin-bottom:8px"><div class="avatar av-c1">' + (l.name ? l.name.slice(0, 2).toUpperCase() : '?') + '</div>' +
      '<div style="flex:1;min-width:0"><div class="t-strong lead-name">' + (l.name || '—') + '</div>' +
      '<div class="t-meta t-mono lead-contact">' + (l.phone || '') + (l.email ? ' · ' + l.email : '') + '</div></div>' +
      '<span class="pill pill-info lead-course">' + (l.courseId || 'General') + '</span></div>' +
      '<div class="row" style="margin-top:8px;gap:8px;flex-wrap:wrap;align-items:center">' +
      '<span class="t-mono t-meta" style="font-size:11px">' + (l.source || 'manual') + '</span>' +
      '<span class="pill pill-warn lead-assigned" style="font-size:11px">' + escapeHtml(assignee) + '</span>' +
      '<span class="spacer"></span>' +
      '<select class="form-input lead-assign-sel" style="max-width:200px;min-height:32px;padding:4px 8px;font-size:12px" data-assign="' + l.id + '"><option value="">Reassign to…</option>' + opts + '</select>' +
      (l.status !== 'won' ? '<button type="button" class="btn btn-sm btn-success lead-btn-won" data-lead-status="won">✓ Won</button>' : '<span class="pill pill-success">Won</span>') +
      (l.status !== 'lost' ? '<button type="button" class="btn btn-sm btn-danger lead-btn-lost" data-lead-status="lost">✕ Lost</button>' : '<span class="pill" style="background:rgba(179,58,42,.1);color:#B33A2A">Lost</span>') +
      '</div></article>';
  }).join('');
  const pills = STAGES.map((s) => {
    const count = leads.filter((l) => l.status === s).length;
    return '<button class="seg-item ' + (s === stage ? 'active' : '') + '" data-stage="' + s + '">' + s + ' <span class="t-mono">' + count + '</span></button>';
  }).join('');
  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Leads</div><h1 class="t-h1">Leads inbox</h1><p class="t-meta">' + (leadsLoading ? 'loading…' : leads.length + ' total · ' + productionMembers.length + ' assignees') + '</p></div></div>' +
    '<nav class="seg-nav">' + pills + '</nav>' +
    '<div style="margin-top:16px">' + (cards || '<div class="card card-pad-lg empty"><div class="empty-illus">📭</div><h2>No ' + stage + ' leads yet</h2><p>Publish techtatwa.in and its Apply form feeds this inbox. Leads also flow in from the Pipeline manually.</p></div>') + '</div>'
  );
}

export function bindLeadsInbox(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const stageBtn = t.closest<HTMLElement>('[data-stage]');
    if (stageBtn && stageBtn.dataset.stage) { location.hash = '#/leads-inbox?stage=' + stageBtn.dataset.stage; (window as any).__rerender?.(); return; }
    const statusBtn = t.closest<HTMLElement>('[data-lead-status]');
    if (statusBtn) {
      const card = statusBtn.closest<HTMLElement>('[data-lead-id]');
      if (!card || !card.dataset.leadId) return;
      const id = card.dataset.leadId;
      const next = statusBtn.dataset.leadStatus as 'won' | 'lost';
      // OPTIMISTIC: update local cache so the user sees the change instantly,
      // then sync with Firestore. Failure → revert.
      const lead = (productionLeads || []).find((x) => x.id === id);
      const prev = lead ? lead.status : undefined;
      if (lead) lead.status = next;
      const stageChanged = (window as any).__stageRecount;
      try { (window as any).__rerender?.(); } catch {}
      void updateLeadStatus(id, next)
        .then(() => {
          toast('Lead marked ' + next + ' · ' + (lead?.name || ''), 'success');
          // Move it out of current stage: filter again on next paint
          productionLeads = null;
          (window as any).__rerender?.();
        })
        .catch((err) => {
          if (lead && prev) lead.status = prev;
          (window as any).__rerender?.();
          toast(err instanceof Error ? err.message : 'Could not update — kept previous status', 'warn');
        });
      void stageChanged;
      return;
    }
  });

  document.addEventListener('change', (e) => {
    const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('[data-assign]');
    if (!sel || !sel.dataset.assign || !sel.value) return;
    const id = sel.dataset.assign;
    const uid = sel.value;
    const lead = (productionLeads || []).find((x) => x.id === id);
    const prevOwner = lead ? lead.ownerUid : undefined;
    if (lead) { lead.ownerUid = uid; lead.status = 'assigned'; }
    const member = productionMembers.find((m) => m.uid === uid);
    void assignLead(id, uid)
      .then(() => {
        toast('Assigned to ' + (member ? member.name : uid.slice(0, 6)), 'success');
        // Remove this card from "new" stage since it's now assigned.
        productionLeads = null;
        (window as any).__rerender?.();
      })
      .catch((err) => {
        if (lead && prevOwner !== undefined) { lead.ownerUid = prevOwner; lead.status = 'new'; }
        if (sel) sel.value = prevOwner || '';
        (window as any).__rerender?.();
        toast(err instanceof Error ? err.message : 'Assignment failed', 'warn');
      });
  });
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
